// Reset…, Erase…, Export, Ban… and Unban of the player page (spec/dashboard.md section 11.2; spec/api-conventions.md sections
// 17.2–17.4 and 21.3): commands, never staged. Reset writes the release defaults of the typed collections; Erase removes the
// player for good and opens the receipt; Export downloads the per-user document; Ban (a reason, an optional end) makes every
// sign-in and player route of the game API answer 403 "banned" until Unban or the end.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ops } from '../../api/operations';
import type { ErasureReceipt, Player, ResetResult } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { downloadText } from '../../content/localization/strings';
import { collectionTemplate } from '../../players/model';
import type { ReleaseInfo } from '../../players/releaseCatalog';
import { invalidatePlayer } from '../../players/usePlayer';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Checkbox, TextArea, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { reasonError } from '../../ui/ReasonDialog';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput } from '../../ui/values';
import { queryCache } from '../../workspace/cache';
import { secondsToIso } from '../settings/keys';
import { bumpText, toastCommandError } from './commands';
import { ForceField, ReasonField } from './RestoreDialogs';
import { SkeletonRows } from '../../ui/PageStates';

interface DialogProps {
  env: string;
  player: Player;
  open: boolean;
  onClose: () => void;
}

export function ResetDialog({ release, ...props }: DialogProps & { release: ReleaseInfo }) {
  return (
    <Modal title="Reset the player" open={props.open} onClose={props.onClose} size="m">
      {props.open && <ResetBody {...props} release={release} />}
    </Modal>
  );
}

function ResetBody({ env, player, onClose, release }: DialogProps & { release: ReleaseInfo }) {
  const { client } = useAuth();
  const { org, game } = useGame();
  const toasts = useToasts();
  const typed = player.collections.map((c) => c.collection).filter((c) => !!collectionTemplate(c, release.templates));
  const [ticked, setTicked] = useState<Set<string>>(() => new Set(typed));
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const chosen = typed.filter((c) => ticked.has(c));
  const valid = chosen.length > 0 && !reasonError(reason);
  const toggle = (c: string, on: boolean) =>
    setTicked((s) => {
      const next = new Set(s);
      if (on) next.add(c);
      else next.delete(c);
      return next;
    });
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const body = { reason: reason.trim(), force: player.online ? force : false, ...(chosen.length === typed.length ? {} : { collections: chosen }) };
      const r = (await client.data(ops.resetPlayer, { path: { org, game: game.slug, env, player: player.id }, body: body })) as unknown as ResetResult;
      const parts = r.reset.map((c) => {
        const bump = bumpText(c.bump);
        return bump ? `${c.collection} (${bump})` : c.collection;
      });
      toasts.success(parts.length ? `Reset ${parts.join(', ')}` : 'Nothing was reset');
      invalidatePlayer(game.id, env, player.id);
      onClose();
    } catch (e) {
      toastCommandError(toasts, e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="reset-dialog" className="command-dialog">
      <p>Writes the defaults of the pinned release into every section of the ticked collections. Nothing is deleted: the history keeps the former values, so a restore undoes it.</p>
      {release.loading ? (
        <SkeletonRows rows={2} label="Reading the pinned release" />
      ) : typed.length === 0 ? (
        <p className="muted" role="note">
          {release.noPin ? 'No release is pinned on this environment: no collection can be reset.' : 'The pinned release types none of this player’s collections: nothing can be reset.'}
        </p>
      ) : (
        <fieldset className="reset-collections" aria-label="Collections to reset">
          <legend className="field-label">Collections</legend>
          {typed.map((c) => (
            <Checkbox key={c} label={c} checked={ticked.has(c)} onChange={(e) => toggle(c, e.target.checked)} />
          ))}
        </fieldset>
      )}
      <ReasonField value={reason} onChange={setReason} />
      <ForceField player={player} value={force} onChange={setForce} />
      <footer className="modal-footer form-footer command-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={() => void submit()} disabled={!valid} busy={busy}>
          Reset
        </Button>
      </footer>
    </div>
  );
}

export function EraseDialog(props: DialogProps) {
  return (
    <Modal title="Erase the player" open={props.open} onClose={props.onClose} size="m">
      {props.open && <EraseBody {...props} />}
    </Modal>
  );
}

function EraseBody({ env, player, onClose }: DialogProps) {
  const { client } = useAuth();
  const { org, game } = useGame();
  const toasts = useToasts();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [typedId, setTypedId] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = !reasonError(reason) && typedId.trim() === player.id;
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const receipt = (await client.data(ops.erasePlayer, { path: { org, game: game.slug, env, player: player.id }, body: { reason: reason.trim(), confirm: typedId.trim() } })) as unknown as ErasureReceipt;
      queryCache.invalidate(`branch:${game.id}:env:${env}:players:recent`);
      toasts.success('The player was erased');
      onClose();
      navigate(routes.receipt(org, game.slug, env, receipt.id));
      invalidatePlayer(game.id, env, player.id); // a later visit reads the 404, never the cached player
    } catch (e) {
      toastCommandError(toasts, e);
      setBusy(false);
    }
  };
  return (
    <div data-testid="erase-dialog" className="command-dialog">
      <p>
        Erasing <code>{player.id}</code> cannot be undone: the id never resolves again, and a device that signs in again becomes a new player.
      </p>
      <div className="erase-lists">
        <div>
          <h3>Removed</h3>
          <ul className="small">
            <li>every binding (device, Nutaku, custom, name)</li>
            <li>every session and pending handshake</li>
            <li>every save and every history version (and the stored values no one else references)</li>
            <li>the presence stamps</li>
          </ul>
        </div>
        <div>
          <h3>Kept</h3>
          <ul className="small">
            <li>the orders, unlinked from the player, as financial records</li>
            <li>the erasure receipt (counts only, nothing personal) and the audit row</li>
            <li>the database backups, until their window elapses</li>
          </ul>
        </div>
      </div>
      <ReasonField value={reason} onChange={setReason} />
      <Field label="Type the player id to confirm" required>
        <TextInput aria-label="Type the player id to confirm" value={typedId} onChange={(e) => setTypedId(e.target.value)} autoComplete="off" spellCheck={false} />
      </Field>
      <footer className="modal-footer form-footer command-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={() => void submit()} disabled={!valid} busy={busy}>
          Erase
        </Button>
      </footer>
    </div>
  );
}

export function ExportDialog(props: DialogProps) {
  return (
    <Modal title="Export the player" open={props.open} onClose={props.onClose}>
      {props.open && <ExportBody {...props} />}
    </Modal>
  );
}

function ExportBody({ env, player, onClose }: DialogProps) {
  const { client } = useAuth();
  const { org, game } = useGame();
  const toasts = useToasts();
  const [history, setHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const data = await client.data(ops.exportPlayer, { path: { org, game: game.slug, env, player: player.id }, query: { history: history ? 'true' : 'false' } });
      const filename = `player-${player.id}.json`;
      downloadText(filename, JSON.stringify(data, null, 2), 'application/json');
      toasts.success(`Downloaded ${filename}`);
      onClose();
    } catch (e) {
      toastCommandError(toasts, e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="export-dialog" className="command-dialog">
      <p className="muted small">One JSON document: the player, its bindings, its sessions and the current value of every key — the per-user export.</p>
      <Checkbox label="Include history" checked={history} onChange={(e) => setHistory(e.target.checked)} />
      <footer className="modal-footer form-footer command-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void submit()} busy={busy}>
          Download
        </Button>
      </footer>
    </div>
  );
}

export const BAN_REASON_MAX = 500;

export function BanDialog(props: DialogProps) {
  return (
    <Modal title="Ban the player" open={props.open} onClose={props.onClose} size="m">
      {props.open && <BanBody {...props} />}
    </Modal>
  );
}

function BanBody({ env, player, onClose }: DialogProps) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const toasts = useToasts();
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const n = reason.trim().length;
  const valid = n >= 1 && n <= BAN_REASON_MAX && (until === null || until * 1000 > Date.now());
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const iso = secondsToIso(until);
      await client.data(ops.banPlayer, { path: { org, game: game.slug, env, player: player.id }, body: iso ? { reason: reason.trim(), until: iso } : { reason: reason.trim() } });
      toasts.success(iso ? 'The player is banned until the chosen time' : 'The player is banned');
      invalidatePlayer(game.id, env, player.id);
      onClose();
    } catch (e) {
      toastCommandError(toasts, e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="ban-dialog" className="command-dialog">
      <p>Every sign-in and every player request of the game API answers 403 "banned" while the ban holds. Nothing of the player is changed or deleted.</p>
      <Field label="Reason" required help={`1 to ${BAN_REASON_MAX} characters; kept on the player and in the audit.`} error={n > BAN_REASON_MAX ? `At most ${BAN_REASON_MAX} characters.` : undefined}>
        <TextArea aria-label="Reason" value={reason} rows={3} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Field label="Until" help="Optional; empty bans until Unban. A time in the past is refused.">
        <DateTimeInput aria-label="Until" allowEmpty ctx={{ offsets }} value={until} onCommit={setUntil} />
      </Field>
      <footer className="modal-footer form-footer command-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={() => void submit()} disabled={!valid} busy={busy}>
          Ban
        </Button>
      </footer>
    </div>
  );
}

export function UnbanDialog(props: DialogProps) {
  return (
    <Modal title="Unban the player" open={props.open} onClose={props.onClose}>
      {props.open && <UnbanBody {...props} />}
    </Modal>
  );
}

function UnbanBody({ env, player, onClose }: DialogProps) {
  const { client } = useAuth();
  const { org, game } = useGame();
  const toasts = useToasts();
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await client.data(ops.unbanPlayer, { path: { org, game: game.slug, env, player: player.id } });
      toasts.success('The player is no longer banned');
      invalidatePlayer(game.id, env, player.id);
      onClose();
    } catch (e) {
      toastCommandError(toasts, e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="unban-dialog" className="command-dialog">
      <p>The player can sign in again at once{player.ban?.reason ? ` (banned for: ${player.ban.reason})` : ''}.</p>
      <footer className="modal-footer form-footer command-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void submit()} busy={busy}>
          Unban
        </Button>
      </footer>
    </div>
  );
}
